import { Component, inject, input, signal } from '@angular/core';
import { FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  DokaButton,
  DokaModal,
  DokaModalController,
  DokaSelect,
  DokaSelectOption,
  DokaTextField,
} from '@doka/angular-core';
import { I18NextEagerPipe } from 'angular-i18next';
import { CreateCatalogItemFormType } from './create-catalog-item.form.type';
import {
  CatalogClient,
  CreateCatalogItemModel,
  ICreateCatalogItemModel,
  Inventory_NumbersClient,
} from 'src/api/services/generated/api-clients';
import { rxResource } from '@angular/core/rxjs-interop';
import { DokaI18NextService } from '@doka/i18next';
import { firstValueFrom } from 'rxjs';

@Component({
  templateUrl: 'create-catalog-item-slide-over.component.html',
  imports: [
    DokaModal,
    I18NextEagerPipe,
    FormsModule,
    ReactiveFormsModule,
    DokaTextField,
    DokaSelect,
    DokaButton,
    DokaSelectOption,
  ],
})
export class CreateCatalogItemSlideOverComponent {
  private readonly catalogClient = inject(CatalogClient);
  private readonly invNumbersClient = inject(Inventory_NumbersClient);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly fb = inject(NonNullableFormBuilder);
  protected readonly i18n = inject(DokaI18NextService);

  public readonly divisionId = input<string | undefined>(undefined);

  protected isLoading = signal(false);

  protected readonly catalogCategories = rxResource({
    params: () => ({ divisionId: this.divisionId() }),
    stream: ({ params }) => this.catalogClient.getCatalogCategories(params.divisionId),
  });

  protected readonly inventoryNumbers = rxResource({
    stream: () => this.invNumbersClient.getInventoryNumbers(),
  });

  protected readonly form: FormGroup<CreateCatalogItemFormType> = this.fb.group({
    name: this.fb.control('', [Validators.required, Validators.maxLength(200)]),
    categoryId: this.fb.control<string>('', [Validators.required]),
    inventoryNumberId: this.fb.control('', [Validators.required]),
  });

  protected closeModal() {
    this.modalCtrl.dismiss();
  }

  protected async submit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value: ICreateCatalogItemModel = {
      ...this.form.getRawValue(),
      isoLanguageCode: this.i18n.selectedLanguage,
    };

    this.isLoading.set(true);
    try {
      await firstValueFrom(this.catalogClient.createCatalogItem(new CreateCatalogItemModel(value)));

      this.modalCtrl.dismiss(true, 'created');
    } catch (error) {
      console.error('Error creating catalog item', error);
    } finally {
      this.isLoading.set(false);
    }
  }
}
